/**
 * Keyboard and pointer-lock mouse input, and enough touch to walk about.
 *
 * Translates browser events into the engine-neutral `InputState` the player
 * controller consumes. Porting to a native engine means rewriting this file
 * and nothing downstream of it.
 *
 * **The touch half is for looking at a world, not for playing in one.** A
 * phone has no pointer to lock, so pressing «Почати гру» on one grew a world
 * and left the player on a pause screen with nothing to press. What is here
 * is what it takes to start, walk to the thing you wanted to look at, and
 * knock a block out of it: a thumb-stick, a drag to look, a tap to dig and a
 * press to place. Everything else - the bag, crafting, the hotbar, sneaking,
 * the drone - is still keyboard and mouse, and saying so is more use than
 * half a set of buttons over the view.
 */

import type { InputState } from "../core/player/controller.js";
import { WheelSteps } from "../core/input/wheel.js";

export class Input {
  readonly state: InputState = {
    forward: false, back: false, left: false, right: false,
    jump: false, sneak: false, sprint: false, crouch: false,
  };

  /** Accumulated mouse delta since the last `consumeLook()`. */
  private lookX = 0;
  private lookY = 0;

  locked = false;

  /**
   * A finger is driving instead of a pointer.
   *
   * The game runs while this is true the same way it runs while the pointer
   * is locked: it is what tells the overlay to get out of the way on a
   * machine that can never lock one.
   */
  touching = false;

  /** How far a thumb may stray before a tap becomes a drag, in pixels. */
  private static readonly TAP_SLOP = 12;
  /** How long a finger has to rest before a tap becomes a press, in ms. */
  private static readonly PRESS_MS = 450;
  /** How far from the middle of the stick counts as full speed, in pixels. */
  private static readonly STICK_REACH = 48;

  /** The finger walking, if there is one, and where it went down. */
  private stick: { id: number; x: number; y: number } | null = null;
  /** The finger looking, if there is one, and where it was last seen. */
  private look: {
    id: number; x: number; y: number; from: number; moved: boolean;
  } | null = null;

  /**
   * Fired for one-shot key presses (not held movement keys). Kept separate
   * from `state` because "toggle something" and "am I holding W" are
   * different questions - polling a toggle would fire it every tick.
   */
  onAction: ((action: string) => void) | null = null;

  /** Fired when a number key selects a hotbar slot. Zero-based. */
  onHotbar: ((slot: number) => void) | null = null;

  private readonly canvas: HTMLElement;
  private readonly onLockChange: (locked: boolean) => void;

  constructor(canvas: HTMLElement, onLockChange: (locked: boolean) => void) {
    this.canvas = canvas;
    this.onLockChange = onLockChange;

    window.addEventListener("keydown", this.onKey);
    window.addEventListener("keyup", this.onKey);
    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mousedown", this.onMouseDown);
    document.addEventListener("mouseup", this.onMouseUp);
    document.addEventListener("wheel", this.onWheel, { passive: true });
    // Right-click places blocks, so the browser menu has to stay out of it.
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());

    /*
     * Touch, on the canvas rather than on the window.
     *
     * Anything over the panels is somebody reaching for a control, and the
     * panels are not children of the canvas - so listening here is what
     * keeps a tap on «Назад» from also being a tap on the world behind it.
     *
     * Not passive: a drag across a game has to not scroll the page, and a
     * long press has to not offer to copy the canvas.
     */
    canvas.addEventListener("touchstart", this.onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", this.onTouchMove, { passive: false });
    canvas.addEventListener("touchend", this.onTouchEnd, { passive: false });
    canvas.addEventListener("touchcancel", this.onTouchEnd, { passive: false });
  }

  /**
   * Take the pointer back.
   *
   * Browsers refuse this for about a second after Escape released the lock, as
   * an anti-trap measure. Without the retry, closing a panel with Escape drops
   * the player on the click-to-play screen for no reason they can see - so one
   * delayed second attempt, and only one.
   */
  requestLock(): void {
    const result: unknown = this.canvas.requestPointerLock();
    if (result instanceof Promise) {
      result.catch(() => {
        window.setTimeout(() => {
          const retry: unknown = this.canvas.requestPointerLock();
          if (retry instanceof Promise) retry.catch(() => { /* give up quietly */ });
        }, 1300);
      });
    }
  }

  private onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    if (!this.locked) {
      // Drop held keys, otherwise you keep walking after tabbing away.
      for (const k of Object.keys(this.state) as (keyof InputState)[]) {
        this.state[k] = false;
      }
      /*
       * And whatever the right button was holding down.
       *
       * A player who presses escape while winding the требушет would come
       * back still holding a wound arm, and the first thing they did would
       * be to release a button nobody was pressing.
       */
      this.onAction?.("placeEnd");
      // And whatever the left button was digging, for the same reason.
      this.onAction?.("breakEnd");
    }
    this.onLockChange(this.locked);
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    this.lookX += e.movementX;
    this.lookY += e.movementY;
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (!this.locked) return;
    if (e.button === 0) this.onAction?.("break");
    else if (e.button === 2) this.onAction?.("place");
  };

  /**
   * And letting go of the right button, which almost nothing cares about.
   *
   * The требушет does: you hold to wind the arm and let go to throw, and how
   * long you held is how hard it throws. This is the only place in the game
   * where releasing a button means anything - a click has always been one
   * event - and it shipped *missing*, so the engine wound up and never
   * loosed until something else took the pointer away and the release below
   * fired instead. "Заряджається але не стріляє, при чому стріляє якщо
   * відкрити та закрити інвентар."
   */
  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 2) this.onAction?.("placeEnd");
    /*
     * And the left one, since blocks grew durability.
     *
     * A block is worked at for as long as the button is down - "hits count
     * only if you hold the button" - so letting go is now an event the game
     * needs, exactly as the требушет's release is. Sent whether or not the
     * game has the pointer: a button released while the page was not
     * listening is still a button that is no longer down.
     */
    else if (e.button === 0) this.onAction?.("breakEnd");
  };

  /**
   * Mouse wheel scrolls the hotbar selection.
   *
   * Called at most once per event and at most once every MIN_STEP_MS - see
   * core/input/wheel.ts for why a Magic Mouse makes that necessary.
   */
  onScroll: ((delta: number) => void) | null = null;

  private readonly wheel = new WheelSteps();

  private onWheel = (e: WheelEvent): void => {
    if (!this.locked) {
      // A gesture that happened while the game was not listening must not be
      // waiting in the accumulator when it starts again.
      this.wheel.reset();
      return;
    }
    const step = this.wheel.step(e.deltaY, e.deltaMode, performance.now());
    if (step !== 0) this.onScroll?.(step);
  };

  private onKey = (e: KeyboardEvent): void => {
    const down = e.type === "keydown";

    /*
     * Typing is typing, not walking.
     *
     * There was nothing to type into while the game was up until the комора
     * got a search box - and with the pointer locked nothing can have focus,
     * so this changes nothing about playing. Escape is let through on
     * purpose: it is how you get out of whatever you are typing in.
     */
    const into = e.target as HTMLElement | null;
    if (into !== null && e.code !== "Escape" &&
      (into.tagName === "INPUT" || into.tagName === "TEXTAREA" ||
        into.isContentEditable)) {
      return;
    }

    // Ctrl/Cmd shortcuts are checked before the movement keys, so Ctrl+S does
    // not also register as walking backwards.
    if (e.ctrlKey || e.metaKey) {
      const shortcut =
        e.code === "KeyS" ? "save" :
        e.code === "KeyE" ? "exportSave" :
        e.code === "KeyO" ? "importSave" :
        null;
      if (shortcut !== null) {
        e.preventDefault();
        if (down && !e.repeat) this.onAction?.(shortcut);
        return;
      }
    }

    switch (e.code) {
      case "KeyW": case "ArrowUp":    this.state.forward = down; break;
      case "KeyS": case "ArrowDown":  this.state.back = down; break;
      case "KeyA": case "ArrowLeft":  this.state.left = down; break;
      case "KeyD": case "ArrowRight": this.state.right = down; break;
      case "Space":                   this.state.jump = down; e.preventDefault(); break;
      /*
       * Shift crouches, held; Z runs.
       *
       * They were the other way round - Shift ran, because somebody once
       * pressed Shift expecting to run - until they were swapped on request:
       * "поміняй місцями, що робить кнопка Shift та кнопка Z". Shift under
       * the little finger is where a crouch is held in most games, and a
       * run is the thing you tap.
       */
      case "ShiftLeft": case "ShiftRight": this.state.crouch = down; break;
      case "KeyZ":                    this.state.sneak = down; break;
      case "ControlLeft": case "KeyR":     this.state.sprint = down; break;

      // One-shot actions - fire on press only, ignore auto-repeat.
      case "KeyT":
        if (down && !e.repeat) this.onAction?.("timeSpeed");
        break;
      case "KeyN":
        if (down && !e.repeat) this.onAction?.("skipToNight");
        break;
      case "KeyF":
        if (down && !e.repeat) this.onAction?.("toggleFly");
        break;
      // Back into your own body. The drone stays where it is, flying.
      case "KeyG":
        if (down && !e.repeat) this.onAction?.("landDrone");
        break;
      case "KeyE":
        if (down && !e.repeat) this.onAction?.("toggleInventory");
        break;
      case "KeyC":
        if (down && !e.repeat) this.onAction?.("toggleCamera");
        break;
      case "KeyK":
        if (down && !e.repeat) this.onAction?.("toggleSkins");
        break;
      case "KeyQ":
        // Q throws one; with shift or control held, the whole stack. The
        // same pair of gestures the inventory already uses for "move one"
        // and "move the lot".
        if (down && !e.repeat) {
          this.onAction?.(
            e.shiftKey || e.ctrlKey || e.metaKey ? "dropStack" : "dropItem");
        }
        break;
      case "KeyB":
        if (down && !e.repeat) this.onAction?.("toggleRecipes");
        break;
      case "Escape":
        // Fires as the browser is already releasing the pointer.
        if (down) this.onAction?.("escape");
        break;

      // Hotbar. Digit1..Digit9 -> slots 0..8.
      case "Digit1": case "Digit2": case "Digit3":
      case "Digit4": case "Digit5": case "Digit6":
      case "Digit7": case "Digit8": case "Digit9":
        if (down && !e.repeat) {
          this.onHotbar?.(Number(e.code.slice(5)) - 1);
        }
        break;
      default: return;
    }
  };

  /** Returns and clears the pending mouse delta. */
  /**
   * Where the thumb-stick is, for whatever draws it. Null when nobody is
   * walking.
   */
  get stickAt(): { x: number; y: number; dx: number; dy: number } | null {
    const held = this.stick;
    if (held === null) return null;
    return { x: held.x, y: held.y, dx: this.stickDx, dy: this.stickDy };
  }

  private stickDx = 0;
  private stickDy = 0;

  /**
   * The left third of the screen walks; the rest looks.
   *
   * A side rather than a drawn circle somebody has to find: the stick
   * appears under the thumb that asked for it, which is the only way it is
   * ever in the right place on a screen whose size nobody chose.
   */
  private isStickSide(x: number): boolean {
    return x < window.innerWidth / 3;
  }

  private onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    this.touching = true;
    for (const touch of Array.from(e.changedTouches)) {
      if (this.isStickSide(touch.clientX) && this.stick === null) {
        this.stick = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
        continue;
      }
      if (this.look === null) {
        this.look = {
          id: touch.identifier, x: touch.clientX, y: touch.clientY,
          from: Date.now(), moved: false,
        };
      }
    }
    this.onLockChange(this.locked);
  };

  private onTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    for (const touch of Array.from(e.changedTouches)) {
      const held = this.stick;
      if (held !== null && touch.identifier === held.id) {
        /*
         * Held keys, not a speed.
         *
         * The controller downstream takes "am I holding W", so the stick is
         * turned into those - which means walking is the same walking
         * whether a thumb or a keyboard asked for it, and nothing below this
         * file has to know a phone exists.
         */
        const dx = touch.clientX - held.x;
        const dy = touch.clientY - held.y;
        const reach = Input.STICK_REACH;
        this.stickDx = Math.max(-1, Math.min(1, dx / reach));
        this.stickDy = Math.max(-1, Math.min(1, dy / reach));
        const dead = 0.25;
        this.state.forward = this.stickDy < -dead;
        this.state.back = this.stickDy > dead;
        this.state.left = this.stickDx < -dead;
        this.state.right = this.stickDx > dead;
        // Pushed to the edge is a run, which is how a stick has always said
        // it on every controller anybody has held.
        this.state.sprint = Math.hypot(this.stickDx, this.stickDy) > 0.9;
        continue;
      }
      const looking = this.look;
      if (looking !== null && touch.identifier === looking.id) {
        this.lookX += touch.clientX - looking.x;
        this.lookY += touch.clientY - looking.y;
        if (Math.hypot(touch.clientX - looking.x, touch.clientY - looking.y) > 0) {
          const far = Math.hypot(
            touch.clientX - looking.x, touch.clientY - looking.y);
          if (far > 0) looking.moved = looking.moved || far > 0;
        }
        looking.x = touch.clientX;
        looking.y = touch.clientY;
      }
    }
  };

  private onTouchEnd = (e: TouchEvent): void => {
    e.preventDefault();
    for (const touch of Array.from(e.changedTouches)) {
      const held = this.stick;
      if (held !== null && touch.identifier === held.id) {
        this.stick = null;
        this.stickDx = 0;
        this.stickDy = 0;
        this.state.forward = false;
        this.state.back = false;
        this.state.left = false;
        this.state.right = false;
        this.state.sprint = false;
        continue;
      }
      const looking = this.look;
      if (looking === null || touch.identifier !== looking.id) continue;
      /*
       * A tap digs; resting a moment places.
       *
       * There is no second mouse button on a finger, and a game where you
       * can take the world apart and not put it back is half a game. Which
       * of the two it was is decided by how long the finger stayed still -
       * the same distinction a mouse makes with a click and a hold.
       */
      const far = Math.hypot(
        touch.clientX - looking.x, touch.clientY - looking.y);
      const still = !looking.moved && far <= Input.TAP_SLOP;
      if (still) {
        const held = Date.now() - looking.from;
        if (held >= Input.PRESS_MS) {
          /*
           * A finger that rested places - and on the требушет, the press and
           * the release arrive together, so a finger throws at whatever the
           * wind reached in one frame. A phone cannot wind an engine; it can
           * still fire one.
           */
          this.onAction?.("place");
          this.onAction?.("placeEnd");
        } else {
          /*
           * A tap is one blow, and then the finger is off the block.
           *
           * There is no holding a block down on a phone: the hold gesture is
           * already how you place. So digging by finger is tapping, and
           * `Digging` keeps what a tap landed for a moment afterwards so a
           * rhythm of taps gets through a block. See FORGET_SECONDS.
           */
          this.onAction?.("break");
          this.onAction?.("breakEnd");
        }
      }
      this.look = null;
    }
  };

  consumeLook(): readonly [number, number] {
    const out = [this.lookX, this.lookY] as const;
    this.lookX = 0;
    this.lookY = 0;
    return out;
  }
}
